/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { ccEnum } from '../Utility/CCEnum';
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import IDependency from './IDependency';
import StateEvent from './StateEvent';
import { StateID } from './StateID';
import StateManager from './StateManager';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Component representing a state, which when becaomes active
 * exectues a body of work and does not complete until all depencies
 * are also complete.
 */
@cc._decorator.ccclass
@cc._decorator.disallowMultiple
export default class StateComponent extends ReferencedComponent
{
    /** Unique key per type for injection reference. */
    @property(
        {
            visible: (function(): boolean
            {
                return this.stateID === StateID.Custom;
            }),
            override: true
        })
    public InstanceId: string = "";

    /** @hidden */
    @property({ type: ccEnum.StateID, visible: true })
    protected stateID: StateID = StateID.Initializing;
    /** Accessor for Enum representing core state types and custom. */
    public get StateID(): StateID { return this.stateID; }
    /** Mutator for Enum representing core state types and custom. */
    public set StateID(value: StateID) { this.stateID = value; }

    /** @hidden */
    @property({ type: StateComponent, visible: true })
    private _nextState: StateComponent = null;
    /** Accessor to State that follows this in execution order. */
    public get NextState(): StateComponent { return this._nextState; }
    /** Mutator for State that follows this in execution order. */
    public set NextState(state: StateComponent) { this._nextState = state; }

    /** @hidden */
    @property({ type: StateComponent, visible: true })
    private _optionalNextState: StateComponent = null;
    /** Accessor to State that could optionally follow this. */
    public get OptionalNextState(): StateComponent { return this._optionalNextState; }

    /** @hidden */
    @property({ type: StateManager, visible: true })
    protected stateManager: StateManager = null;
    /** Accessor to [[StateManager]] to which this state belongs. */
    public get StateManager(): StateManager { return this.stateManager; }
    /** Mutator to [[StateManager]] to which this state belongs. */
    public set StateManager(manager: StateManager) { this.stateManager = manager; }

    /** @hidden */
    private _dependencies: IDependency[] = [];
    /** List of objects to which this cannot complete until each is complete. */
    public get Dependencies(): IDependency[] { return this._dependencies; }

    /** @hidden */
    private _isOptionalNext: boolean = false;
    /** Accessor to flag, that when true indicates next state should be optional state. */
    public get IsOptionalNext(): boolean { return this._isOptionalNext; }
    /** Mutator of flag, that when true indicates next state should be optional state. */
    public set IsOptionalNext(value) { this._isOptionalNext = value; }

    /** @hidden */
    protected onLoad(): void
    {
        this.InstanceId = StateComponent.GetStateID(this.StateID, this.InstanceId);
        super.onLoad();
    }

    /** @hidden */
    protected start(requiresStateManager: boolean = true): void
    {
        super.start();

        if (requiresStateManager && !this.stateManager)
        {
            cc.error(`Cannot find parent StateManger for node [${this.node.name}]`);
            return;
        }
    }

    /** Checks whether state matches [[StateManager]] current state. */
    public IsCurrentState(): boolean
    {
        return this.StateManager.CurrentState === this;
    }

    /**
     * Adds dependency as requirement for this states completion.
     * @param dependency Object for which this state will become dependent.
     */
    public AddDependency(dependency: IDependency): void
    {
        this._dependencies.push(dependency);
    }

    /**
     * Removes dependency as requirement for this states completion.
     * @param dependency Object for which this state will become dependent.
     */
    public RemoveDependency(dependency: IDependency): void
    {
        this._dependencies = this._dependencies.filter(dep => dep !== dependency);
    }

    /** @hidden */
    public OnStateChange(fnOnEvent: (event: StateEvent) => any): void
    {
        let eventName: string = StateEvent.GetStateEventName(this.stateID);
        this.node.on(eventName, fnOnEvent);
    }

    /** @hidden */
    public OffStateChange(fnOnEvent: (event: StateEvent) => any): void
    {
        let eventName: string = StateEvent.GetStateEventName(this.stateID);
        if (this.node) { this.node.off(eventName, fnOnEvent); }
    }

    /** Tru if contains dependencies. */
    public HasDependencies(): boolean
    {
        return this._dependencies.length > 0;
    }

    /** @hidden */
    public toString(): string
    {
        // tslint:disable-next-line:max-line-length
        return `State [${StateID[this.stateID]}] Next [${(this._nextState) ? StateID[this._nextState.stateID] : 'none' }]`;
    }

    /** @hidden */
    public get name(): string
    {
        return StateID[this.StateID] || "uknown";
    }

    public static GetStateID(stateID: StateID, customStateID: string = null): string
    {
        let key: string = `State.${StateID[stateID]}`;
        return stateID === StateID.Custom && customStateID
            ? `${key}.${customStateID}`
            : key;
    }
}
